import { Button } from "primereact/button";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import { Skeleton } from "primereact/skeleton";
import { Toolbar } from "primereact/toolbar";
import { useRef, useState, useEffect } from "react";
import AbstractEditDialog from "./AbstractEditDialog";
import { Message } from "primereact/message";
import { DataTable } from "../../../components/data/DataTable";
import { Column } from "primereact/column";

export interface DataColumn<T> {
  field?: keyof T;
  expression?: (item: T) => any;
  header: string;
  sortable?: boolean;
  filter?: boolean;
  className?: string;
  type?: string;
}

export interface AbstractListProps<T, C = T> {
  api: {
    useGetListQuery: (() => any) | any;
    useUpdateItemMutation: () => any;
    useDeleteItemMutation: () => any;
  };
  columns: DataColumn<T>[];
  childColumns?: DataColumn<C>[];
  rowsNumber?: number;
  editDialog: {
    renderForm: (formik: any) => JSX.Element;
    validationSchema: any;
    item?: any;
  };
  exportFilename?: string;
  title?: string;
  idField?: string;
  childrenField?: string;
  childrenEditable?: boolean;
  childrenShowHeader?: boolean;
  editDialogWidth?: string;
}

const AbstractNomenclature = <
  T extends {
    [key: string]: any;
  },
  C extends {
    [key: string]: any;
  } = T,
>({
  api,
  columns,
  childColumns,
  rowsNumber,
  editDialog,
  editDialogWidth,
  exportFilename,
  title,
  idField,
  childrenField = "children",
  childrenEditable = true,
  childrenShowHeader = false,
}: AbstractListProps<T, C>) => {
  const queryResult = typeof api.useGetListQuery === "function" ? api.useGetListQuery() : api.useGetListQuery;

  const { data, error, isLoading, isFetching } = queryResult;
  const updateMutation = api.useUpdateItemMutation();
  const [deleteAction, { isLoading: isDeleting }] = api.useDeleteItemMutation();

  const dt = useRef<any>(null);
  const [dialog, setDialog] = useState<{
    open: boolean;
    item: T | null;
  }>({
    open: false,
    item: editDialog?.item ?? null,
  });

  const [expandedRows, setExpandedRows] = useState<any[]>([]);

  const openEditDialog = (item?: T | null) => {
    setDialog({ open: true, item: !item ? (editDialog.item ?? null) : item });
  };

  const closeDialog = () => {
    setDialog({ open: false, item: null });
  };

  const handleDialogSubmit = (values: Partial<T>) => {
    console.log("Dialog submit", values);
  };

  const handleDelete = (item: T) => {
    deleteAction(item[idField ?? "id"]);
  };

  const confirmDelete = (event: React.MouseEvent, item: T) => {
    confirmPopup({
      target: event.currentTarget as EventTarget & HTMLElement,
      message: "Сигурни ли сте, че искате да изтриете този запис?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Да",
      rejectLabel: "Не",
      accept: () => handleDelete(item),
    });
  };

  const handleExport = () => {
    if (dt.current) {
      console.log(dt.current);
      dt.current.exportCSV({ selectionOnly: true });
    }
  };

  const actionButtons = (rowData: T) => (
    <div className="flex flex-row gap-1">
      <Button
        icon="pi pi-pencil"
        severity="warning"
        rounded
        size="small"
        className="h-2rem w-2rem"
        onClick={() => openEditDialog(rowData)}
      />
      <Button
        icon="pi pi-trash"
        severity="danger"
        rounded
        size="small"
        className="h-2rem w-2rem"
        onClick={(e) => confirmDelete(e, rowData)}
      />
    </div>
  );

  const rowExpansionTemplate = (data: T) => {
    const childrenData = data[childrenField] as C[];
    return (
      childrenData &&
      childrenData.length > 0 && (
        <DataTable
          value={childrenData}
          dataKey={idField}
          paginator={false}
          showGridlines={false}
          showHeaders={childrenShowHeader}
          stripedRows={false}
          className="ml-5 my-4"
        >
          {(childColumns || columns).map((column, index) => (
            <Column
              key={index}
              field={column.field as string}
              dataType={column.type}
              header={childrenShowHeader ? column.header : undefined}
              filter={column.filter}
              body={(rowData: C) => {
                if (column.expression) {
                  return column.expression(rowData as any);
                }
                return column.type === "boolean"
                  ? rowData[column.field as keyof C]
                    ? "Да"
                    : "Не"
                  : rowData[column.field as keyof C];
              }}
              className={column.className}
            />
          ))}
          {childrenEditable && <Column body={(rowData: C) => actionButtons(rowData as any)} />}
        </DataTable>
      )
    );
  };

  useEffect(() => {
    if (data) {
      const initialExpandedRows = data.data.filter((item: T) => item[childrenField] && item[childrenField].length > 0);
      setExpandedRows(initialExpandedRows);
    }
  }, [data, childrenField]);

  const topButtons = (
    <>
      <Button label="Добави" icon="pi pi-plus" severity="success" onClick={() => openEditDialog()} />
    </>
  );

  const extraButtons = (
    <>
      <Button label="Експорт CSV" icon="pi pi-upload" severity="secondary" onClick={handleExport} />
    </>
  );

  if (isLoading) return <Skeleton className="w-full h-2rem" />;

  if (error) return <Message severity="error" text={JSON.stringify(error)} />;

  return (
    <>
      {title && <h2 className="py-0 my-2">{title}</h2>}
      <Toolbar start={topButtons} end={extraButtons} />
      <DataTable
        ref={dt}
        value={data?.data ?? []}
        emptyMessage="Не бяха открити данни."
        exportFilename={exportFilename}
        expandedRows={expandedRows}
        onRowToggle={(e: any) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        rows={rowsNumber ?? 50}
        rowsPerPageOptions={[25, 50, 100]}
      >
        {columns.map((column, index) => (
          <Column
            key={index}
            field={column.field as string}
            header={column.header}
            sortable={column.sortable}
            filter={column.filter}
            dataType={column.type}
            body={(rowData) => {
              if (column.expression) {
                return column.expression(rowData);
              }
              return column.type === "boolean" ? (rowData[column.field] ? "Да" : "Не") : rowData[column.field];
            }}
            className={column.className}
          />
        ))}
        <Column header="Действия" body={actionButtons} />
      </DataTable>

      {dialog.open && (
        <AbstractEditDialog
          item={dialog.item}
          onDialogClose={closeDialog}
          onDialogSubmit={handleDialogSubmit}
          updateMutation={updateMutation}
          validationSchema={editDialog.validationSchema}
          renderForm={(formik) => editDialog.renderForm(formik)}
          width={editDialogWidth}
        />
      )}

      <ConfirmPopup />
    </>
  );
};

export default AbstractNomenclature;
